'use client';

import { useState, useEffect } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { cn } from '@/src/shared/lib/utils';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import { buildApiUrl, buildStaticUrl } from '@/src/shared/api/config';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';
import type { Message } from '../types';
import { OrderCard } from './OrderCard';

// Trusted domains whose links are displayed as clickable cards
const TRUSTED_URL_REGEX =
  /https?:\/\/((?:www\.)?(docs\.google\.com|drive\.google\.com|photos\.google\.com|photo\.google\.com|photos\.app\.goo\.gl|goo\.gl|googleusercontent\.com|lh[3-6]\.googleusercontent\.com|(?:www\.)?dropbox\.com|onedrive\.live\.com|1drv\.ms|sharepoint\.com))[^\s]*/gi;

type TrustedLinkType =
  | 'google_doc'
  | 'google_sheet'
  | 'google_slide'
  | 'google_drive'
  | 'google_photo'
  | 'dropbox'
  | 'onedrive'
  | 'unknown';

interface TrustedLink {
  url: string;
  type: TrustedLinkType;
  label: string;
}

function detectLinkType(url: string): { type: TrustedLinkType; label: string } {
  const lower = url.toLowerCase();
  if (lower.includes('docs.google.com/document')) return { type: 'google_doc', label: 'Google Doc' };
  if (lower.includes('docs.google.com/spreadsheets')) return { type: 'google_sheet', label: 'Google Sheets' };
  if (lower.includes('docs.google.com/presentation')) return { type: 'google_slide', label: 'Google Slides' };
  if (
    lower.includes('photos.google.com') ||
    lower.includes('photo.google.com') ||
    lower.includes('photos.app.goo.gl') ||
    lower.includes('goo.gl') ||
    lower.includes('googleusercontent.com')
  ) return { type: 'google_photo', label: 'Google Photo' };
  if (lower.includes('drive.google.com')) return { type: 'google_drive', label: 'Google Drive' };
  if (lower.includes('dropbox.com')) return { type: 'dropbox', label: 'Dropbox' };
  if (lower.includes('onedrive.live.com') || lower.includes('1drv.ms') || lower.includes('sharepoint.com')) return { type: 'onedrive', label: 'OneDrive' };
  return { type: 'unknown', label: 'Shared Link' };
}

function extractTrustedLinks(text: string): TrustedLink[] {
  const links: TrustedLink[] = [];
  const regex = new RegExp(TRUSTED_URL_REGEX.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const url = match[0];
    links.push({ url, ...detectLinkType(url) });
  }
  return links;
}

function TrustedLinkIcon({ type }: { type: TrustedLinkType }) {
  if (type === 'google_photo') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    );
  }
  if (type === 'google_drive' || type === 'dropbox' || type === 'onedrive') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
    );
  }
  // Google Doc / Sheet / Slide / unknown
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

// Remove trusted URLs from message body so they don't show as raw text
function stripTrustedLinks(text: string): string {
  return text.replace(new RegExp(TRUSTED_URL_REGEX.source, 'gi'), '').trim();
}

// Direct image URLs (lh3-6.googleusercontent.com) can be loaded as <img src>
function isDirectImageUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return (
      hostname === 'googleusercontent.com' ||
      /^lh[3-6]\.googleusercontent\.com$/.test(hostname)
    );
  } catch {
    return false;
  }
}

interface LinkPreview {
  imageUrl: string | null;
  title: string | null;
}

// Fetch og:image and og:title from backend proxy for a trusted link
function useLinkPreview(url: string, skip: boolean): { preview: LinkPreview | null; loading: boolean } {
  const [preview, setPreview] = useState<LinkPreview | null>(null);
  const [loading, setLoading] = useState(!skip);

  useEffect(() => {
    if (skip) return;
    let cancelled = false;

    setLoading(true);
    fetchWithTokenRefresh(buildApiUrl(`/chat/link-preview?url=${encodeURIComponent(url)}`))
      .then((res) => (res.ok ? res.json() : null))
      .then((data: LinkPreview | null) => {
        if (!cancelled) {
          setPreview(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [url, skip]);

  return { preview, loading };
}

// Wrapper component so each link gets its own hook instance
function TrustedLinkCard({
  link,
  isOwnMessage,
}: {
  link: { url: string; type: TrustedLinkType; label: string };
  isOwnMessage: boolean;
}) {
  const isDirect = isDirectImageUrl(link.url);
  const { preview, loading } = useLinkPreview(link.url, isDirect);

  // Direct googleusercontent images — render immediately as <img>
  if (isDirect) {
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block max-w-sm rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
      >
        <img
          src={link.url}
          alt="Google Photo"
          className="w-full h-auto rounded-lg"
          loading="lazy"
        />
      </a>
    );
  }

  // Loading skeleton
  if (loading) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg animate-pulse',
          isOwnMessage ? 'bg-background-tertiary' : 'bg-background-secondary'
        )}
      >
        <div className="w-10 h-10 bg-gray-300 rounded flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-gray-300 rounded w-3/4" />
          <div className="h-2 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    );
  }

  // Has og:image — show photo preview + label/title
  if (preview?.imageUrl) {
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'block rounded-lg overflow-hidden hover:opacity-90 transition-opacity',
          isOwnMessage ? 'bg-background-tertiary' : 'bg-background-secondary'
        )}
      >
        <img
          src={preview.imageUrl}
          alt={preview.title ?? link.label}
          className="w-full h-auto max-h-64 object-cover"
          loading="lazy"
        />
        <div className="flex items-center gap-2 px-3 py-2">
          <TrustedLinkIcon type={link.type} />
          <p className="flex-1 text-sm font-medium truncate">
            {preview.title ?? link.label}
          </p>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </div>
      </a>
    );
  }

  // No image — show icon + title (if available) + open button
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg hover:opacity-90 transition-opacity',
        isOwnMessage ? 'bg-background-tertiary' : 'bg-background-secondary'
      )}
    >
      <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
        <TrustedLinkIcon type={link.type} />
      </div>
      <p className="flex-1 text-sm font-medium truncate">
        {preview?.title ?? link.label}
      </p>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
}

interface MessageItemProps {
  message: Message;
  className?: string;
}

export const MessageItem = ({ message, className }: MessageItemProps) => {
  const router = useRouter();
  const params = useParams();
  const locale = params.locale as string;
  const activeActor = useAuthStore((state) => state.activeActor);
  const isOwnMessage = activeActor?.id === message.senderActorId;

  // Format time (HH:MM)
  const time = new Date(message.createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // SYSTEM messages are centered
  if (message.type === 'SYSTEM') {
    return (
      <div className={cn('flex justify-center px-5 py-2', className)}>
        <div className="px-4 py-2 bg-background-secondary rounded-full">
          <span className="text-text-secondary text-sm italic">
            {message.body}
          </span>
        </div>
      </div>
    );
  }

  // ORDER_CARD messages
  if (message.type === 'ORDER_CARD' && message.metadata) {
    const metadata = message.metadata as {
      order?: {
        id: string;
        title: string;
        description?: string;
        location?: string;
        deadline?: string;
        startedAt?: string;
      };
      participant?: {
        id: string;
        name: string;
        displayName?: string;
        avatarUrl?: string;
      };
    };

    const orderId = metadata.order?.id;
    const orderUrl = orderId
      ? activeActor?.type === 'WORKER'
        ? `/${locale}/orders/${orderId}`
        : `/${locale}/profile/orders/${orderId}`
      : null;

    return (
      <div className={cn('flex justify-center px-5 py-2', className)}>
        {metadata.order && metadata.participant ? (
          <OrderCard
            order={metadata.order}
            participant={metadata.participant}
            onLocationClick={() => {
              if (metadata.order?.location) {
                console.log('View location:', metadata.order.location);
              }
            }}
            onViewOrder={orderUrl ? () => router.push(orderUrl) : undefined}
          />
        ) : (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <span className="text-yellow-800 text-base">
              Order data unavailable
            </span>
          </div>
        )}
      </div>
    );
  }

  // TEXT messages - bubble style
  return (
    <div
      className={cn(
        'flex px-5 py-1',
        isOwnMessage ? 'justify-end' : 'justify-start',
        className
      )}
    >
      <div
        className={cn(
          'max-w-[630px] px-4 py-2 inline-flex justify-between items-start gap-2.5',
          isOwnMessage
            ? 'bg-background-secondary rounded-tl-[10px] rounded-bl-2xl rounded-br-[10px]'
            : 'bg-background-tertiary rounded-tr-[10px] rounded-bl-[10px] rounded-br-2xl'
        )}
      >
        {/* Message Text */}
        <div className="flex-1 min-w-0">
          {(() => {
            const bodyText = message.body ? stripTrustedLinks(message.body) : '';
            return bodyText.length > 0 ? (
              <span className="text-text-primary text-base font-normal font-['Roboto'] leading-5 whitespace-pre-wrap break-words">
                {bodyText}
                {message.isEdited && (
                  <span className="text-text-secondary text-sm ml-2">(edited)</span>
                )}
              </span>
            ) : message.isEdited ? (
              <span className="text-text-secondary text-sm">(edited)</span>
            ) : null;
          })()}

          {/* Trusted link cards (Google Docs/Drive/Photos, Dropbox, OneDrive) */}
          {message.body && (() => {
            const links = extractTrustedLinks(message.body);
            if (links.length === 0) return null;
            return (
              <div className="mt-2 space-y-2">
                {links.map((link, idx) => (
                  <TrustedLinkCard key={idx} link={link} isOwnMessage={isOwnMessage} />
                ))}
              </div>
            );
          })()}

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-2 space-y-2">
              {message.attachments.map((ma) => {
                const att = ma.attachment;
                const isImage = att.mimeType.startsWith('image/');
                // All chat attachments (images and documents) use protected endpoint
                const fileUrl = buildApiUrl(`/chat/attachments/${att.id}/url`);

                return (
                  <div key={ma.id}>
                    {isImage ? (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block max-w-sm rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                      >
                        <img
                          src={fileUrl}
                          alt={att.fileName}
                          className="w-full h-auto rounded-lg"
                          loading="lazy"
                        />
                      </a>
                    ) : (
                      <a
                        href={buildApiUrl(`/chat/attachments/${att.id}/url`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg hover:opacity-90 transition-opacity',
                          isOwnMessage ? 'bg-background-tertiary' : 'bg-background-secondary'
                        )}
                      >
                        <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {att.fileName}
                          </p>
                          <p className="text-xs text-text-secondary">
                            {(att.fileSize / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="flex-shrink-0"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </a>
                    )}

                    {/* Virus scan warning */}
                    {att.virusScanStatus === 'INFECTED' && (
                      <p className="text-xs text-red-600 mt-1">
                        This file failed virus scan
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Moderation warning */}
          {message.isModerated && (
            <p className="text-xs text-warning mt-1">
              This message was moderated
            </p>
          )}
        </div>

        {/* Time - inside bubble, right aligned */}
        <span className="text-text-secondary text-base font-normal font-['Roboto'] leading-5 flex-shrink-0 self-end">
          {time}
        </span>
      </div>
    </div>
  );
};
